import { useCurrentSortedAlerts } from '@backoffice-frontend/alerts-common'
import { NotInServiceReason } from '@backoffice-frontend/graphql'
import { useTheme } from '@backoffice-frontend/patterns'
import { isBookable } from '@backoffice-frontend/vehicle-common'
import { ToolsIcon } from '@moia-dev/pace-icons'
import { Icon, Text } from '@moia-dev/pace-web'
import { useFlatTireSimulationActive } from '../../utils/flatTireSimulation'
import { AlertAssignmentIcon } from '../AlertAssignmentIcon'
import { HandledByIcon } from '../HandledByIcon'
import type { VehicleListOverlayRowVehicleFragment } from './VehicleListOverlayRow.hook'

type VehicleListOverlayRowProps = {
  vehicle: VehicleListOverlayRowVehicleFragment
  onClick: () => void
  isSelected: boolean
}

export const VehicleListOverlayRow = ({
  vehicle,
  onClick,
  isSelected,
}: VehicleListOverlayRowProps) => {
  const theme = useTheme()
  const { label, taskNotifications, fccCurrentAdAlerts } = vehicle
  const sortedAlerts = useCurrentSortedAlerts(
    taskNotifications?.tasks,
    fccCurrentAdAlerts?.alerts,
  )
  const hasFlatTire = useFlatTireSimulationActive(vehicle.id)
  const isUnknownLocation =
    vehicle.notInServiceReasons?.includes(
      NotInServiceReason.NoRecentValidLocation,
    ) ?? false

  return (
    <div
      role="button"
      tabIndex={-1}
      css={{
        display: 'flex',
        padding: `${theme.space.Space2} ${theme.space.Space3}`,
        alignItems: 'center',
        gap: `${theme.space.Space1}`,
        cursor: 'pointer',
        borderBottom: `1px solid ${theme.semantic.ColorBorderOnSelectedDefault}`,
        '&:last-child': {
          borderBottom: 'none',
        },
        '&:hover': {
          h6: {
            color: theme.semantic.ColorActionPrimaryAlternativeHovered,
          },
        },
        '&:focus, &:focus-visible': {
          outline: 'none',
        },
      }}
      onClick={onClick}
    >
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.space.Space2,
        }}
      >
        <ColoredDot
          isBookable={isBookable(vehicle)}
          isUnknownLocation={isUnknownLocation}
        />
        <Text
          weight="bold"
          size="lg"
          css={{
            width: 48,
            color: isSelected ? theme.semantic.ColorAccentDefault : undefined,
          }}
        >
          {label}
        </Text>
      </div>
      <div
        css={{
          display: 'flex',
          width: 20,
          height: 20,
          alignItems: 'center',
          gap: 10,
        }}
      >
        {hasFlatTire && (
          <Icon size={16}>
            <ToolsIcon css={{ color: theme.semantic.ColorContentReduced }} />
          </Icon>
        )}
        {vehicle.fccHandledByOperator ? (
          <HandledByIcon
            operatorName={`${vehicle.fccHandledByOperator.firstName} ${vehicle.fccHandledByOperator.lastName}`}
          />
        ) : (
          <AlertAssignmentIcon enableTooltip alerts={sortedAlerts} />
        )}
      </div>
    </div>
  )
}

const ColoredDot = ({
  isBookable,
  isUnknownLocation,
}: {
  isBookable: boolean
  isUnknownLocation: boolean
}) => {
  const theme = useTheme()

  const color = isUnknownLocation
    ? theme.semantic.ColorSurfaceCritical
    : isBookable
      ? theme.semantic.ColorSuccessDefault
      : theme.semantic.ColorCriticalDefault

  const borderColor = isUnknownLocation
    ? isBookable
      ? theme.semantic.ColorSuccessDefault
      : theme.semantic.ColorCriticalDefault
    : undefined

  return (
    <span
      css={{
        width: 8,
        height: 8,
        borderRadius: 100,
        background: color,
        border: borderColor ? `1px solid ${borderColor}` : undefined,
      }}
    />
  )
}
